// Route Result — renders the P3 journey exactly as the backend computed it.
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Check, Circle, Loader2, Bookmark } from 'lucide-react';
import { CTA_ROUTES, MODE_EXPLAIN, trackGps, getJourneyToken } from '../lib/gpsShared';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AutoLifestyleRoute() {
  const { journeyId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('t') || getJourneyToken(journeyId);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/auto-lifestyle/gps/journey/${journeyId}?t=${encodeURIComponent(token)}`,
        { credentials: 'include' });
      if (r.status === 403) { setErr(token ? 'This route belongs to another traveler.' : 'This route needs its access link — open it from your original route URL or sign in.'); return; }
      if (!r.ok) { setErr('Route not found.'); return; }
      const d = await r.json();
      setData(d);
      trackGps('gps_route_viewed', journeyId);
    } catch (_) { setErr('Could not load your route.'); }
  }, [journeyId, token]);
  useEffect(() => { load(); }, [load]);

  const saveRoute = async () => {
    setSaving(true); setSaveMsg('');
    try {
      const r = await fetch(`${API}/api/auto-lifestyle/gps/journey/${journeyId}/claim`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ t: token }),
      });
      if (r.status === 401) {
        navigate(`/portal/login?return=${encodeURIComponent(`/auto-lifestyle/route/${journeyId}?t=${token}`)}`);
        return;
      }
      const d = await r.json();
      if (d.success) { setSaveMsg('Route saved to your ACE account.'); trackGps('gps_route_saved', journeyId); load(); }
      else setSaveMsg('We could not save this route automatically — ask your concierge.');
    } catch (_) { setSaveMsg('Save failed — please try again.'); }
    setSaving(false);
  };

  if (err) return (
    <div className="min-h-screen bg-white flex flex-col">      <main className="flex-1 flex items-center justify-center px-5">
        <div className="text-center" data-testid="gps-route-error">
          <p className="text-lg font-semibold mb-4">{err}</p>
          <Link to="/auto-lifestyle" className="text-red-700 text-sm uppercase tracking-widest">Map a new route →</Link>
        </div>
      </main></div>
  );
  if (!data) return (
    <div className="min-h-screen bg-white flex flex-col">      <main className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-red-700" /></main>
      </div>
  );

  const { route, next_stop, destination, priorities, current_location } = data;
  const cta = CTA_ROUTES[next_stop?.step_type] || null;
  const stepIcon = (s) => s.status === 'done'
    ? <Check className="h-4 w-4 text-green-600" />
    : s.step_id === next_stop?.step_id
      ? <ArrowRight className="h-4 w-4 text-red-700" />
      : <Circle className="h-3 w-3 text-gray-300" />;

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
            <main className="flex-1 max-w-3xl mx-auto w-full px-5 py-12 sm:py-16 pb-28 sm:pb-16" data-testid="gps-route-result">
        <p className="text-xs uppercase tracking-[0.35em] text-red-700 mb-3">♠ Your Auto Lifestyle Route</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-none mb-3" data-testid="gps-route-mode">
          {route.route_mode}
        </h1>
        <p className="text-sm text-gray-600 mb-8 max-w-lg" data-testid="gps-mode-explain">
          {MODE_EXPLAIN[route.route_mode]}
        </p>

        <div className="border-t border-gray-200 py-5" data-testid="gps-current-location">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-2">Current Location</p>
          <p className="text-sm text-gray-800">{current_location.summary}</p>
          {current_location.more_info_needed?.map((m, i) => (
            <p key={i} className="text-xs text-gray-500 mt-1.5 italic">{m}</p>
          ))}
        </div>

        <div className="border-t border-gray-200 py-5" data-testid="gps-destination">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-2">Destination</p>
          <p className="text-base font-semibold">{destination.text}</p>
          {priorities?.length > 0 && (
            <p className="text-xs text-gray-500 mt-1.5 uppercase tracking-widest">{priorities.join(' · ')}</p>
          )}
        </div>

        <div className="border-t border-gray-200 py-5" data-testid="gps-route-reasons">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-3">Why This Route</p>
          <ul className="space-y-2">
            {route.route_reasons.map((r, i) => (
              <li key={i} className="text-sm text-gray-800 flex gap-2.5">
                <span className="text-red-700 mt-0.5">♠</span>{r}
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-gray-200 py-5" data-testid="gps-roadmap">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-4">Your Roadmap</p>
          <ol className="space-y-4">
            {route.steps.map(s => (
              <li key={s.step_id} className="flex gap-3.5" data-testid={`gps-roadstep-${s.step_order}`}>
                <div className="mt-1 shrink-0 w-5 flex justify-center">{stepIcon(s)}</div>
                <div className={s.step_id === next_stop?.step_id ? '' : 'opacity-70'}>
                  <p className={`text-sm ${s.step_id === next_stop?.step_id ? 'font-semibold' : ''}`}>
                    {s.plain_language_action}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{s.reason}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="border-t-2 border-black pt-7 mt-2" data-testid="gps-next-stop">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-2">Next Stop</p>
          <p className="text-xl font-bold mb-5">{next_stop?.plain_language_action}</p>
          {cta ? (
            <Link to={cta.path} onClick={() => trackGps('gps_next_stop_clicked', journeyId)}
              data-testid="gps-next-stop-cta"
              className="inline-flex items-center gap-2 bg-red-700 text-white px-8 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-black transition-colors">
              {cta.label} <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <p className="text-sm text-gray-600" data-testid="gps-no-action-state">
              No action needed right now. Save this route, explore at your pace, or return anytime — your route can change when your Auto Lifestyle does.
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-5">
            {!data.saved && (
              <button onClick={saveRoute} disabled={saving} data-testid="gps-save-route-btn"
                className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-black border border-gray-300 hover:border-black px-5 py-2.5 transition-colors">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bookmark className="h-4 w-4" />} Save My Route
              </button>
            )}
            {data.saved && <span className="text-xs uppercase tracking-widest text-green-700" data-testid="gps-saved-badge">Saved to your account</span>}
            <Link to="/auto-lifestyle" className="text-xs uppercase tracking-widest text-gray-500 hover:text-black">Map a new route</Link>
            <Link to="/contact" className="text-xs uppercase tracking-widest text-gray-500 hover:text-black">Ask Your Auto Concierge</Link>
          </div>
          {saveMsg && <p className="text-xs text-gray-600 mt-3" data-testid="gps-save-msg">{saveMsg}</p>}
        </div>
      </main>
          </div>
  );
}
